import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";
import { emptyApi } from "./emptyApi";
import qs from "qs";
import { Task, TaskStatsCount } from "types/Models/task";
import { Pagination } from "types/Models/pagination";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { number } from "prop-types";
import {
  EQUAL,
  GREATER_THAN,
  LESSER_THAN,
  GREATER_THAN_EQUAL,
  LESSER_THAN_EQUAL,
} from "constants/task";
import {
  ActiveEntity,
  AppSubscriptionCounts,
  CompanyOverviewResponse,
  EntityOverviewPayload,
  ParsedListForBulkCart,
} from "types/Models/serviceTeam";
import { FilingListTab } from "constants/agentDashboard";
import { makeFormData } from "utils/makeFormData";
import { AdHocTask, NonAdHocTask } from "types/Models/taskTemplate";
import { AddBulkCartItem } from "constants/BulkCartAction";

type ServiceTeam = {
  image_url: string;
  name: string;
  uuid: string;
  assigned_task_count?: number;
  is_suggested?: boolean;
};

export type DomainResponse = {
  name: string;
  uuid: string;
  domain: string;
  status: string;
};

type GetAllServiceProviderTaskRequest = {
  page_num?: number | null;
  page_size?: number | null;
  search_term?: string | null;
  task_tag_filter_uuids?: string | null;
  company_tag_filter_uuids?: string | null;
  task_template_filter_uuids?: string | null;
  task_state_filter_uuids?: string | null;
  assignee_uuids?: string | null;
  subscription_filter?: string | null;
  service_team_ids?: string | null;
  team_member_uuids?: string | null;
  show_unassigned_team?: string | null;
  show_unassigned_preparer?: string | null;
  sort_by?: string | null;
  etd_filter?: string | null;
  generate_report?: boolean | null;
  export_to_mail?: boolean | null;
  completed_start_date?: string | null;
  completed_end_date?: string | null;
  formation_type?: string | null;
  profitability_status?: string | null;
  state_registrations?: string | null;
  missing_business_financials_form_7004_flag?: boolean | null;
  missing_information_form_7004_flag?: boolean | null;
  bookkeeping_status?: string | null;
  company_group_filter_uuids?: string | null;
  seasons?: string | null;
  task_category_types?: string | null;
  task_list_type?: string | null;
  foreign_subsidiaries_operator?:
    | typeof EQUAL
    | typeof GREATER_THAN
    | typeof LESSER_THAN
    | typeof GREATER_THAN_EQUAL
    | typeof LESSER_THAN_EQUAL
    | null;
  foreign_subsidiaries_value?: string | null;
  entity_ids?: string | null;
  subscription_status_filter?: FilingListTab | null;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getServiceTeam: build.query<
      ServiceTeam[],
      {
        accessible_teams?: typeof INKLE_TEAM_AND_CPA_TEAM | null;
        serviceTeamId?: string | null;
        suggest_preparer?: boolean | null;
      }
    >({
      query: ({ accessible_teams, serviceTeamId, suggest_preparer }) => {
        let queryUrl = qs.stringify(
          {
            accessible_teams: accessible_teams,
            service_team_ids: serviceTeamId,
            suggest_preparer: suggest_preparer || undefined,
          },
          { skipNulls: true }
        );
        return {
          url: `/api/inkle/serviceteams/get/?${queryUrl}`,
        };
      },
    }),

    createServiceTeam: build.mutation({
      query: ({ payload }) => {
        return {
          url: `/api/inkle/serviceteams/create/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    assignMonthlySummary: build.mutation({
      query: ({ profileId, monthlyTxnSummaryId }) => {
        return {
          url: `/api/inkle/bookkeeping/monthly_txn_summary/${monthlyTxnSummaryId}/assignee/`,
          method: "PUT",
          body: { profile_id: profileId },
        };
      },
      invalidatesTags: (result) => {
        return result ? [{ type: "BOOKS_CRM" }] : [];
      },
    }),

    joinServiceTeam: build.mutation({
      query: ({ groupId }) => {
        return {
          url: `/api/inkle/serviceteams/team/${groupId}/join/`,
          method: "POST",
        };
      },
      invalidatesTags: (result) => (result ? ["DOMAIN_RECOGNITION"] : []),
    }),
    // Non-FOREIGN_CA users join a company group: uuid goes in the body as
    // group_id. (FOREIGN_CA users use joinServiceTeam with uuid in the path.)
    joinCompanyGroup: build.mutation<unknown, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/company/group/service_team/join/`,
        method: "POST",
        body: { group_id: groupId },
      }),
      invalidatesTags: (result) => (result ? ["DOMAIN_RECOGNITION"] : []),
    }),
    getDomainRecognization: build.query<DomainResponse[], void>({
      query: () => "/api/inkle/company/group/service_team/join/",
      providesTags: ["DOMAIN_RECOGNITION"],
    }),

    getAllServiceProviderTask: build.query<
      {
        tasks: Task[];
        counts: {
          all_task_count: number;
          ongoing_count: number;
          archived_count: number;
          completed_count: number;
        };
        task_state_counts: TaskStatsCount;
      } & Pagination & { download_url?: string | null },
      GetAllServiceProviderTaskRequest
    >({
      query: (params) => {
        let queryUrl = qs.stringify(
          {
            ...params,
            completed_end_date: params.completed_end_date
              ? dayjs(params.completed_end_date).format(YYYY_MM_DD)
              : null,
            completed_start_date: params.completed_start_date
              ? dayjs(params.completed_start_date).format(YYYY_MM_DD)
              : null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `api/inkle/tasks/v1/provider/tasks/${queryUrl}`,
        };
      },
      providesTags: (result) =>
        result ? ["All_SERVICE_PROVIDERS", "NOTES", "Tasks"] : [],
    }),

    updateTaskAssigneeAdmin: build.mutation<
      {},
      {
        taskId: string;
        preparerId?: string | null;
        removePreparer?: boolean;
        serviceTeamId?: string;
        removeServiceTeam?: boolean;
        estimatedDeliveryTime?: string;
        reason?: string;
        bookkeeping_task_start_date?: string;
        assigned_support_agent?: string | null;
        pickup_date?: string | null;
        form_1120_global_status?: string | null;
        books_global_status?: string;
        remove_assigned_support_agent?: boolean;
        deadline_date?: string;
        reviewerId?: string;
        removeReviewer?: boolean;
        preparation_complete_date?: string | null;
        review_start_date?: string | null;
        review_end_date?: string | null;
        missing_information?: string | null;
        mi_raised_on?: string | null;
        mi_received_on?: string | null;
        on_hold_reason?: string | null;
        customer_acr?: string | null;
        season?: string;
      }
    >({
      query: ({
        taskId,
        preparerId,
        removePreparer,
        serviceTeamId,
        removeServiceTeam,
        estimatedDeliveryTime,
        reason,
        bookkeeping_task_start_date,
        assigned_support_agent,
        pickup_date,
        form_1120_global_status,
        books_global_status,
        remove_assigned_support_agent,
        deadline_date,
        reviewerId,
        removeReviewer = false,
        preparation_complete_date,
        review_start_date,
        review_end_date,
        missing_information,
        mi_raised_on,
        mi_received_on,
        on_hold_reason,
        customer_acr,
        season,
      }) => {
        const payload = {
          preparer_id: preparerId,
          remove_preparer: removePreparer,
          service_team_id: serviceTeamId,
          remove_service_team: removeServiceTeam,
          estimated_delivery_time: estimatedDeliveryTime,
          reason,
          bookkeeping_task_start_date: bookkeeping_task_start_date,
          customer_acr,
          assigned_support_agent,
          pickup_date,
          form_1120_global_status,
          books_global_status,
          remove_assigned_support_agent,
          deadline_date,
          reviewer_id: reviewerId,
          remove_reviewer: removeReviewer,
          preparation_complete_date,
          review_start_date,
          review_end_date,
          missing_information,
          mi_raised_on,
          mi_received_on,
          on_hold_reason,
          season,
        };

        return {
          url: `/api/inkle/tasks/v1/${taskId}/update/admin/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "All_SERVICE_PROVIDERS",
              "Tasks",
              "TaskList",
              "BOOKKEEPING_TASKS",
              "TASK_1120_TRACKER",
              "AGENT_ACTIONS",
            ]
          : [],
    }),

    updateTaskAssigneeProvider: build.mutation<
      {},
      {
        taskId: string;
        preparerId?: string | null;
        removePreparer?: boolean;
        estimatedDeliveryTime?: string;
        reason?: string;
        assigned_support_agent?: string | null;
        pickup_date?: string | null;
        form_1120_global_status?: string | null;
        books_global_status?: string;
        remove_assigned_support_agent?: boolean;
        deadline_date?: string;
      }
    >({
      query: ({
        taskId,
        preparerId,
        removePreparer,
        estimatedDeliveryTime,
        reason,
        assigned_support_agent,
        pickup_date,
        form_1120_global_status,
        books_global_status,
        remove_assigned_support_agent,
        deadline_date,
      }) => {
        const payload = {
          preparer_id: preparerId,
          remove_preparer: removePreparer,
          estimated_delivery_time: estimatedDeliveryTime,
          reason,
          assigned_support_agent,
          pickup_date,
          form_1120_global_status,
          books_global_status,
          remove_assigned_support_agent,
          deadline_date,
        };
        return {
          url: `/api/inkle/tasks/v1/${taskId}/update/provider/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "All_SERVICE_PROVIDERS",
              "BOOKKEEPING_TASKS",
              "TASK_1120_TRACKER",
              "AGENT_ACTIONS",
            ]
          : [],
    }),
    getAllCpaTeams: build.query<ServiceTeam[], {}>({
      query: () => {
        return {
          url: `/api/inkle/serviceteams/all_cpa_team_members/`,
          method: "GET",
        };
      },
    }),
    getEntitiesOverview: build.query<
      CompanyOverviewResponse,
      EntityOverviewPayload | null
    >({
      query: (props) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/dashboards/v1/admin-dashboard/company_overview/${queryUrl}`,
          method: "GET",
        };
      },
    }),
    getAllActiveEntities: build.query<ActiveEntity[], void>({
      query: () => "/api/inkle/company/active_us_entities/",
    }),
    getAppSubscriptionDetails: build.query<AppSubscriptionCounts, void>({
      query: () =>
        "/api/inkle/dashboards/v1/admin-dashboard/subscription_overview/",
    }),
    parseBulkCartActionCSV: build.mutation<
      {
        current_page: number;
        per_page: number;
        template_details: ParsedListForBulkCart[];
        total_count: number;
        total_pages: number;
      },
      {
        pageNum: number;
        file?: File | null;
      }
    >({
      query: ({ file, pageNum }) => {
        const query = qs.stringify(
          { page_num: pageNum },
          { addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/billing/cart/bulk_action/file_verify/${query}`,
          body: makeFormData({ file }),
          method: "POST",
        };
      },
    }),
    addBulkTask: build.mutation<
      Task[],
      {
        file: File | null;
        productItems: AddBulkCartItem[];
      }
    >({
      query: ({ file, productItems }) => {
        return {
          url: `/api/inkle/billing/cart/bulk_action/`,
          body: makeFormData({
            file,
            product_items: JSON.stringify(productItems),
          }),
          method: "POST",
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetServiceTeamQuery,
  useCreateServiceTeamMutation,
  useJoinServiceTeamMutation,
  useJoinCompanyGroupMutation,
  useAssignMonthlySummaryMutation,
  useGetDomainRecognizationQuery,
  useLazyGetServiceTeamQuery,
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
  useGetAllServiceProviderTaskQuery,
  useLazyGetAllServiceProviderTaskQuery,
  useGetAllCpaTeamsQuery,
  useGetEntitiesOverviewQuery,
  useGetAllActiveEntitiesQuery,
  useGetAppSubscriptionDetailsQuery,
  useParseBulkCartActionCSVMutation,
  useAddBulkTaskMutation,
} = extendedApi;
